import { useEffect, useState } from 'react'
import { Outlet, useLocation } from 'react-router-dom'
import { useAuth } from '@/hooks/useAuth'
import { Sidebar } from './Sidebar'
import { TopBar } from './TopBar'
import { UserMenu } from './UserMenu'
import { cn } from '@/lib/cn'

/** Header on top, side menu on the left, page content between them — the layout */

const SIDEBAR_KEY = 'p24.sidebar.collapsed'

const TITLES: Record<string, string> = {
  '/': 'Overview',
  '/chatbot': 'Chatbot',
  '/users': 'Users',
  '/settings': 'Settings',
  '/audit': 'Audit log',
}

export function AppLayout() {
  const { user, permissions } = useAuth()
  const location = useLocation()

  /** The ONLY thing this app puts in localStorage. */
  const [collapsed, setCollapsed] = useState(() => localStorage.getItem(SIDEBAR_KEY) === '1')
  const [mobileOpen, setMobileOpen] = useState(false)

  useEffect(() => {
    localStorage.setItem(SIDEBAR_KEY, collapsed ? '1' : '0')
  }, [collapsed])

  // Close the off-canvas drawer on navigation, or a tap on a nav item leaves.
  useEffect(() => { setMobileOpen(false) }, [location.pathname])

  if (!user) return null

  const title = TITLES[matchTopLevel(location.pathname)] ?? 'Paisa24'

  return (
    <div className="flex h-dvh overflow-hidden bg-sunken">
      <div className="hidden md:block">
        <Sidebar
          permissions={permissions}
          collapsed={collapsed}
          onToggleCollapse={() => { setCollapsed((c) => !c) }}
        />
      </div>

      {mobileOpen && (
        <div className="fixed inset-0 z-40 md:hidden">
          <button
            type="button"
            aria-label="Close menu"
            className="absolute inset-0 bg-black/40"
            onClick={() => { setMobileOpen(false) }}
          />
          <div className="relative h-full w-60">
            <Sidebar
              permissions={permissions}
              collapsed={false}
              onToggleCollapse={() => { setMobileOpen(false) }}
            />
          </div>
        </div>
      )}

      <div className="flex min-w-0 flex-1 flex-col">
        <TopBar
          title={title}
          userMenu={<UserMenu />}
          onOpenSidebar={() => { setMobileOpen(true) }}
        />

        <main className={cn('flex-1 overflow-y-auto')}>
          <div className="mx-auto w-full max-w-[1600px] p-4 md:p-6">
            <Outlet />
          </div>
        </main>
      </div>
    </div>
  )
}

/** "/chatbot/leads/118" -> "/chatbot", so a detail view keeps its parent's title. */
function matchTopLevel(pathname: string): string {
  if (pathname === '/') return '/'
  const segment = pathname.split('/')[1] ?? ''
  return `/${segment}`
}
